// @ts-check
// Blocos do atendimento aberto na Caixa (contrato 10 de atendimento, seções 9, 14, 15, 16, 19 e 24.3): consultas
// internas, Proteção (escalonado em, quem foi avisado, "Registrar notificação ao Conselho Tutelar"), caso de retenção
// (desfecho, pontuação, próximo contato e a Lei 9.870/1999), avaliação e manifestação da Ouvidoria (identidade conforme
// o modo, "Ver identidade" na protegida e respostas públicas).
import { html } from 'htm/preact';
import { useState } from 'preact/hooks';
import { addPublicReply, cancelConsultation, recordCouncilNotification } from '../../data/service-desk.js';
import { formatDateTime, formatShortDate, todayIsoDate } from '../../lib/format.js';
import { Button } from '../../ui/button.js';
import { Icon } from '../../ui/icon.js';
import { showSuccessSnackbar } from '../../ui/snackbar.js';
import { TicketValueTag, serviceErrorMessage, showServiceError } from './service-display.js';
import { t } from '../../i18n/index.js';

/** @param {{ title: string, icon: string, tone?: string, children: any, testId?: string }} props */
export function TicketPanel({ title, icon, tone, children, testId }) {
  return html`<section class="service-ticket-panel" data-tone=${tone} data-testid=${testId}>
    <h3 class="service-ticket-panel-title"><${Icon} name=${icon} size=${14} />${title}</h3>
    ${children}
  </section>`;
}

/**
 * Consultas internas do atendimento, com "Cancelar" para quem perguntou ou o responsável.
 * @param {{ consultations: any[], viewerWorkspaceMemberId: string | null, isOwner: boolean, onChanged: () => void }} props
 */
export function ConsultationsPanel({ consultations, viewerWorkspaceMemberId, isOwner, onChanged }) {
  if (!consultations?.length) return null;
  return html`<${TicketPanel} title=${t('serviceDesk.consultations.panelTitle')} icon="IconMessageQuestion" testId="service-consultations-panel">
    <ul class="service-panel-list">
      ${consultations.map((consultation) => {
        const isOpen = !['RESOLVED', 'CLOSED'].includes(consultation.status);
        const canCancel = isOpen && (isOwner || consultation.askedBy?.workspaceMemberId === viewerWorkspaceMemberId);
        return html`<li key=${consultation.id}>
          <span class="service-panel-line">
            <${TicketValueTag} field="status" value=${consultation.status} />
            <span>${t('serviceDesk.consultations.to', { name: consultation.assignee?.name ?? consultation.team?.name ?? '' })}</span>
          </span>
          <span class="service-panel-detail">${consultation.question ?? consultation.summary ?? ''}</span>
          ${canCancel
            ? html`<${Button} title=${t('serviceDesk.consultations.cancel')} size="small" variant="secondary" onClick=${async () => {
                try {
                  await cancelConsultation(consultation.id);
                  showSuccessSnackbar(t('serviceDesk.consultations.canceled'));
                  onChanged();
                } catch (error) {
                  showServiceError(error);
                }
              }} />`
            : null}
        </li>`;
      })}
    </ul>
  <//>`;
}

/**
 * @param {{ ticketId: string, protection: any, onChanged: () => void }} props
 */
export function ProtectionPanel({ ticketId, protection, onChanged }) {
  const [isFormOpen, setIsFormOpen] = useState(false);
  // hoje no fuso do membro: o dia em UTC já é amanhã depois das 21h de Brasília
  const [notifiedAt, setNotifiedAt] = useState(todayIsoDate());
  const [councilName, setCouncilName] = useState('');
  const [councilProtocol, setCouncilProtocol] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState(/** @type {string | null} */ (null));
  const [isSaving, setIsSaving] = useState(false);
  const save = async () => {
    setIsSaving(true);
    setError(null);
    try {
      await recordCouncilNotification(ticketId, {
        councilNotifiedAt: new Date(`${notifiedAt}T12:00:00`).toISOString(), councilName: councilName.trim(),
        ...(councilProtocol.trim() ? { councilProtocol: councilProtocol.trim() } : {}), ...(notes.trim() ? { notes: notes.trim() } : {}),
      });
      showSuccessSnackbar(t('serviceDesk.protection.councilSaved'));
      setIsFormOpen(false);
      onChanged();
    } catch (requestError) {
      setError(serviceErrorMessage(requestError));
    } finally {
      setIsSaving(false);
    }
  };
  return html`<${TicketPanel} title=${t('serviceDesk.protection.panelTitle')} icon="IconShieldLock" tone="danger" testId="service-protection-panel">
    <dl class="service-panel-facts">
      <div><dt>${t('serviceDesk.protection.escalatedAt')}</dt><dd>${protection?.escalatedAt ? formatDateTime(protection.escalatedAt) : ''}</dd></div>
      <div><dt>${t('serviceDesk.protection.notified')}</dt><dd>${(protection?.escalatedToNames ?? []).filter(Boolean).join(', ') || t('serviceDesk.protection.nobody')}</dd></div>
    </dl>
    ${protection?.councilNotifiedAt
      ? html`<p class="service-panel-ok"><${Icon} name="IconShieldCheck" size=${14} />${t('serviceDesk.protection.councilNotified', {
          date: formatShortDate(protection.councilNotifiedAt), council: protection.councilName ?? '', protocol: protection.councilProtocol ?? t('serviceDesk.protection.noProtocol'),
        })}</p>`
      : html`<p class="service-panel-warning" role="status"><${Icon} name="IconAlertTriangle" size=${14} />${t('serviceDesk.protection.councilPending')}</p>`}
    ${!protection?.councilNotifiedAt && !isFormOpen
      ? html`<${Button} title=${t('serviceDesk.protection.recordCouncil')} icon="IconShieldCheck" size="small" accent="danger" onClick=${() => setIsFormOpen(true)} />`
      : null}
    ${isFormOpen
      ? html`<div class="service-inline-panel">
          <div class="form-field-row">
            <label class="form-field"><span class="form-field-label">${t('serviceDesk.protection.councilDate')}</span>
              <input class="text-input" type="date" value=${notifiedAt} onInput=${(/** @type {Event} */ event) => setNotifiedAt(/** @type {HTMLInputElement} */ (event.currentTarget).value)} /></label>
            <label class="form-field"><span class="form-field-label">${t('serviceDesk.protection.councilProtocol')}</span>
              <input class="text-input" value=${councilProtocol} onInput=${(/** @type {Event} */ event) => setCouncilProtocol(/** @type {HTMLInputElement} */ (event.currentTarget).value)} /></label>
          </div>
          <label class="form-field"><span class="form-field-label">${t('serviceDesk.protection.councilName')}</span>
            <input class="text-input" value=${councilName} placeholder=${t('serviceDesk.protection.councilNamePlaceholder')}
              onInput=${(/** @type {Event} */ event) => setCouncilName(/** @type {HTMLInputElement} */ (event.currentTarget).value)} /></label>
          <label class="form-field"><span class="form-field-label">${t('serviceDesk.protection.councilNotes')}</span>
            <textarea class="text-input service-textarea" rows="2" value=${notes} onInput=${(/** @type {Event} */ event) => setNotes(/** @type {HTMLTextAreaElement} */ (event.currentTarget).value)}></textarea></label>
          ${error ? html`<div class="form-error" role="alert"><${Icon} name="IconAlertTriangle" size=${14} /><span>${error}</span></div>` : null}
          <div class="service-inline-actions">
            <${Button} title=${t('common.cancel')} size="small" variant="secondary" onClick=${() => setIsFormOpen(false)} />
            <${Button} title=${t('serviceDesk.protection.saveCouncil')} size="small" accent="blue" disabled=${!councilName.trim() || !notifiedAt} isLoading=${isSaving} onClick=${save} />
          </div>
        </div>`
      : null}
    <p class="service-panel-detail">${t('serviceDesk.protection.noFamilyNotices')}</p>
  <//>`;
}

/**
 * @param {{ ticket: import('../../data/service-desk.js').ServiceTicket, hasTransferDocumentTicket?: boolean }} props
 */
export function RetentionPanel({ ticket, hasTransferDocumentTicket = false }) {
  const retention = ticket.retention;
  if (!retention) return null;
  return html`<${TicketPanel} title=${t('serviceDesk.retention.panelTitle')} icon="IconHeartHandshake" tone="warning" testId="service-retention-panel">
    <dl class="service-panel-facts">
      <div><dt>${t('serviceDesk.retention.outcome')}</dt><dd><${TicketValueTag} field="retentionOutcome" value=${retention.outcome ?? 'MONITORING'} /></dd></div>
      <div><dt>${t('serviceDesk.retention.score')}</dt><dd>${retention.score ?? 0}</dd></div>
      <div><dt>${t('serviceDesk.retention.nextContact')}</dt><dd>${retention.nextContactAt ? formatDateTime(retention.nextContactAt) : t('serviceDesk.retention.noNextContact')}</dd></div>
    </dl>
    ${hasTransferDocumentTicket ? html`<p class="service-panel-warning"><${Icon} name="IconInfoCircle" size=${14} />${t('serviceDesk.retention.transferLaw')}</p>` : null}
  <//>`;
}

/** @param {{ ticket: import('../../data/service-desk.js').ServiceTicket, csat: any }} props */
export function CsatPanel({ ticket, csat }) {
  if (!ticket.csat && !csat) return null;
  return html`<${TicketPanel} title=${t('serviceDesk.csat.panelTitle')} icon="IconStar" testId="service-csat-panel">
    ${ticket.csat?.rating
      ? html`<p class="service-panel-line"><${TicketValueTag} field="csatRating" value=${ticket.csat.rating} icon="IconStar" />
          ${ticket.csat.answeredAt ? html`<span class="service-panel-detail">${formatShortDate(ticket.csat.answeredAt)}</span>` : null}</p>
        ${ticket.csat.comment ? html`<blockquote class="service-quote">${ticket.csat.comment}</blockquote>` : null}`
      : csat?.skippedReason
        ? html`<p class="service-panel-detail">${t(`serviceDesk.csat.skipped.${csat.skippedReason}`)}</p>`
        : html`<p class="service-panel-detail">${t('serviceDesk.csat.waiting')}</p>`}
  <//>`;
}

/**
 * Manifestação da Ouvidoria: tipo, identidade conforme o modo e respostas públicas (acompanhamento pelo protocolo).
 * @param {{ ticket: import('../../data/service-desk.js').ServiceTicket, manifestation: any, onRevealIdentity: () => void, onChanged: () => void }} props
 */
export function ManifestationPanel({ ticket, manifestation, onRevealIdentity, onChanged }) {
  const [reply, setReply] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  if (!manifestation) return null;
  const sendReply = async () => {
    setIsSaving(true);
    try {
      await addPublicReply(ticket.id, reply.trim());
      showSuccessSnackbar(t('ombudsman.publicReplySent'));
      setReply('');
      onChanged();
    } catch (error) {
      showServiceError(error);
    } finally {
      setIsSaving(false);
    }
  };
  return html`<${TicketPanel} title=${t('ombudsman.panelTitle')} icon="IconSpeakerphone" testId="service-manifestation-panel">
    <dl class="service-panel-facts">
      <div><dt>${t('ombudsman.fields.type')}</dt><dd><${TicketValueTag} field="manifestationType" value=${ticket.manifestation?.type} /></dd></div>
      <div><dt>${t('ombudsman.fields.identityMode')}</dt><dd><${TicketValueTag} field="identityMode" value=${manifestation.identityMode} /></dd></div>
      <div><dt>${t('ombudsman.fields.source')}</dt><dd>${t(`ombudsman.sources.${manifestation.source ?? 'TEAM'}`)}</dd></div>
      <div><dt>${t('ombudsman.fields.contact')}</dt><dd>
        ${manifestation.identityMode === 'ANONYMOUS'
          ? t('ombudsman.anonymousContact')
          : manifestation.contact
            ? html`<span class="service-contact-lines">${[manifestation.contact.name, manifestation.contact.email, manifestation.contact.phone].filter(Boolean).map((line) => html`<span>${line}</span>`)}</span>`
            : html`<${Button} title=${t('ombudsman.revealIdentity')} icon="IconEye" size="small" variant="secondary" onClick=${onRevealIdentity} />`}
      </dd></div>
    </dl>
    ${manifestation.identityMode === 'PROTECTED' && !manifestation.contact ? html`<p class="service-panel-detail">${t('ombudsman.revealHint')}</p>` : null}
    <h4 class="service-panel-subtitle">${t('ombudsman.publicReplies')}</h4>
    ${(manifestation.publicReplies ?? []).length === 0
      ? html`<p class="service-panel-detail">${t('ombudsman.noPublicReplies')}</p>`
      : html`<ul class="service-panel-list">${manifestation.publicReplies.map((/** @type {any} */ publicReply) => html`<li key=${publicReply.id}>
          <span class="service-panel-line"><strong>${publicReply.authorName ?? ''}</strong><span class="service-panel-detail">${formatDateTime(publicReply.createdAt)}</span></span>
          <span>${publicReply.body}</span>
        </li>`)}</ul>`}
    <label class="form-field">
      <span class="form-field-label">${t('ombudsman.newPublicReply')}</span>
      <textarea class="text-input service-textarea" rows="3" value=${reply} maxLength=${4000} placeholder=${t('ombudsman.publicReplyPlaceholder')}
        onInput=${(/** @type {Event} */ event) => setReply(/** @type {HTMLTextAreaElement} */ (event.currentTarget).value)}></textarea>
      <span class="input-hint">${t('ombudsman.publicReplyHint')}</span>
    </label>
    <div class="service-inline-actions"><${Button} title=${t('ombudsman.sendPublicReply')} size="small" accent="blue" disabled=${!reply.trim()} isLoading=${isSaving} onClick=${sendReply} /></div>
  <//>`;
}
